import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, Coach.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiError(err, "Login failed.");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#0a0a0a] text-white flex flex-col"
    >
      <header className="px-6 md:px-12 py-6 border-b border-white/10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="admin-login-back"
          className="flex items-center gap-2 text-sm tracking-widest uppercase text-white/60 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-white/20 flex items-center justify-center">
            <span className="font-display text-lg text-[#FF3B30] leading-none">M</span>
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm tracking-wider">MEÑO COACHING</div>
            <div className="text-[9px] tracking-[0.35em] text-[#FF3B30] uppercase">
              Admin
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#111] border border-white/10 p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 border border-white/15 flex items-center justify-center">
              <Lock size={16} className="text-[#FF3B30]" />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30]">
                Restricted
              </div>
              <h1 className="font-display text-3xl uppercase leading-none mt-1">
                Coach Login
              </h1>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Email
              </label>
              <input
                data-testid="admin-email"
                type="email"
                autoComplete="email"
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/50 block mb-2">
                Password
              </label>
              <input
                data-testid="admin-password"
                type="password"
                autoComplete="current-password"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-[#FF574D] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Signing in…
                </>
              ) : (
                <>Sign In</>
              )}
            </button>
          </div>

          <p className="text-xs text-white/40 mt-8 text-center">
            Authorised personnel only.
          </p>
        </form>
      </div>
    </main>
  );
}
